import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

import Header from '../../components/Header';
import BreadcrumbNavigation from '../../components/BreadcrumbNavigation';
import QuickActionToolbar from '../../components/QuickActionToolbar';
import KPICard from './components/KPICard';
import RecentTransactions from './components/RecentTransactions';
import AlertsSidebar from './components/AlertsSidebar';
import QuickStats from './components/QuickStats';

import { supabase } from '../../lib/supabase';

export default function FinancialDashboard() {
  // Separate loading states for KPIs and charts
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fast KPI data fetching - immediate priority
  const fetchKPIData = useCallback(async () => {
    setError(null);
    
    try {
      // Optimized queries with minimal data and limits for fast KPI display
      const [
        negotiationsResult,
        payablesResult,
        receivablesResult
      ] = await Promise.all([
        // Only essential fields for KPI calculations, limit 25 for speed
        supabase?.from('negotiations')?.select('sale_value, supplier_cost')?.order('created_at', { ascending: false })?.limit(25),
        // Only pending amounts for quick calculation
        supabase?.from('payables')?.select('amount, status, due_date')?.eq('status', 'pending')?.limit(25),
        // Only pending receivables for quick calculation  
        supabase?.from('receivables')?.select('amount, status, due_date')?.eq('status', 'pending')?.limit(25)
      ]);

      const recentNegotiations = negotiationsResult?.data;
      const negotiationsError = negotiationsResult?.error;
      const pendingPayables = payablesResult?.data;
      const payablesError = payablesResult?.error;
      const pendingReceivables = receivablesResult?.data;
      const receivablesError = receivablesResult?.error;

      if (negotiationsError) throw negotiationsError;
      if (payablesError) throw payablesError;
      if (receivablesError) throw receivablesError;

      // Fast calculations for immediate KPI display
      const totalRevenue = recentNegotiations?.reduce((sum, neg) => 
        sum + (parseFloat(neg?.sale_value) || 0), 0) || 0;

      const totalPaidToSuppliers = recentNegotiations?.reduce((sum, neg) => 
        sum + (parseFloat(neg?.supplier_cost) || 0), 0) || 0;

      const netProfit = totalRevenue - totalPaidToSuppliers;

      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const pendingPayablesAmount = pendingPayables?.reduce((sum, p) => 
        sum + (parseFloat(p?.amount) || 0), 0) || 0;

      const overduePayables = pendingPayables?.filter(p => {
        const dueDate = new Date(p?.due_date);
        dueDate?.setHours(0, 0, 0, 0);
        return dueDate < today;
      })?.length || 0;

      const pendingReceivablesAmount = pendingReceivables?.reduce((sum, r) => 
        sum + (parseFloat(r?.amount) || 0), 0) || 0;

      const overdueReceivables = pendingReceivables?.filter(r => {
        const dueDate = new Date(r?.due_date);
        dueDate?.setHours(0, 0, 0, 0);
        return dueDate < today;
      })?.length || 0;

      setKpiData({
        totalRevenue,
        totalPaidToSuppliers,
        netProfit,
        pendingPayables: pendingPayablesAmount,
        overduePayables,
        pendingReceivables: pendingReceivablesAmount,
        overdueReceivables
      });

    } catch (err) {
      console.error('Error loading KPI data:', err);
      setError(err?.message || 'Erro ao carregar dados dos KPIs');
    } finally {
      setKpiLoading(false);
    }
  }, []);

  // Deferred chart data fetching - lower priority, heavier queries
  const fetchChartData = useCallback(async () => {
    try {
      // Delay chart loading to not compete with KPIs
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // More comprehensive data for chart rendering with higher limits
      const [
        negotiationsResult,
        payablesResult,
        receivablesResult
      ] = await Promise.all([
        // Full dataset for accurate chart representation
        supabase?.from('negotiations')?.select('sale_value, supplier_cost, created_at')?.order('created_at', { ascending: false })?.limit(100),
        supabase?.from('payables')?.select('amount, status, due_date, created_at')?.order('created_at', { ascending: false })?.limit(100),
        supabase?.from('receivables')?.select('amount, status, due_date, created_at')?.order('created_at', { ascending: false })?.limit(100)
      ]);

      const allNegotiations = negotiationsResult?.data;
      const allNegotiationsError = negotiationsResult?.error;
      const allPayables = payablesResult?.data;
      const allPayablesError = payablesResult?.error;
      const allReceivables = receivablesResult?.data;
      const allReceivablesError = receivablesResult?.error;

      if (allNegotiationsError) throw allNegotiationsError;
      if (allPayablesError) throw allPayablesError;
      if (allReceivablesError) throw allReceivablesError;

      // More detailed calculations for chart rendering
      const chartTotalRevenue = allNegotiations?.reduce((sum, neg) => 
        sum + (parseFloat(neg?.sale_value) || 0), 0) || 0;

      const chartTotalPaidToSuppliers = allNegotiations?.reduce((sum, neg) => 
        sum + (parseFloat(neg?.supplier_cost) || 0), 0) || 0;

      const chartNetProfit = chartTotalRevenue - chartTotalPaidToSuppliers;

      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const chartPendingPayables = allPayables?.filter(p => p?.status === 'pending')?.reduce((sum, p) => 
        sum + (parseFloat(p?.amount) || 0), 0) || 0;

      const chartOverduePayables = allPayables?.filter(p => {
        const dueDate = new Date(p?.due_date);
        dueDate?.setHours(0, 0, 0, 0);
        return p?.status === 'pending' && dueDate < today;
      })?.length || 0;

      const chartPendingReceivables = allReceivables?.filter(r => r?.status === 'pending')?.reduce((sum, r) => 
        sum + (parseFloat(r?.amount) || 0), 0) || 0;

      const chartOverdueReceivables = allReceivables?.filter(r => {
        const dueDate = new Date(r?.due_date);
        dueDate?.setHours(0, 0, 0, 0);
        return r?.status === 'pending' && dueDate < today;
      })?.length || 0;

      setChartData({
        totalRevenue: chartTotalRevenue,
        totalPaidToSuppliers: chartTotalPaidToSuppliers,
        netProfit: chartNetProfit,
        pendingPayables: chartPendingPayables,
        overduePayables: chartOverduePayables,
        pendingReceivables: chartPendingReceivables,
        overdueReceivables: chartOverdueReceivables
      });

    } catch (err) {
      console.error('Error loading chart data:', err);
      // Don't set error state for chart loading failures - KPIs are more important
    } finally {
      setChartLoading(false);
    }
  }, []);

  // Full refresh function
  const refreshDashboard = async () => {
    setRefreshing(true);
    setKpiLoading(true);
    setChartLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchKPIData(),
        fetchChartData()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Load KPIs immediately for fast initial display
    fetchKPIData();
    
    // Defer chart loading for better perceived performance
    const chartTimeout = setTimeout(() => {
      fetchChartData();
    }, 100);
    
    return () => clearTimeout(chartTimeout);
  }, [fetchKPIData, fetchChartData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  // KPI cards data - uses fast-loaded KPI data
  const kpiCards = kpiData ? [
    {
      id: 1,
      title: 'Faturamento Total',
      value: formatCurrency(kpiData?.totalRevenue || 0),
      change: 'Total de vendas',
      trend: 'up',
      icon: 'TrendingUp',
      color: '#10b981'
    },
    {
      id: 2,
      title: 'Pago ao Fornecedor',
      value: formatCurrency(kpiData?.totalPaidToSuppliers || 0),
      change: 'Total de pagamentos',
      trend: 'down',
      icon: 'TrendingDown',
      color: '#ef4444'
    },
    {
      id: 3,
      title: 'Receita Líquida',
      value: formatCurrency(kpiData?.netProfit || 0),
      change: 'Lucro total',
      trend: kpiData?.netProfit > 0 ? 'up' : 'down',
      icon: 'DollarSign',
      color: kpiData?.netProfit > 0 ? '#10b981' : '#ef4444'
    },
    {
      id: 4,
      title: 'Pagamentos a Realizar',
      value: formatCurrency(kpiData?.pendingPayables || 0),
      change: `${kpiData?.overduePayables || 0} vencidos`,
      trend: kpiData?.overduePayables > 0 ? 'down' : 'neutral',
      icon: 'CreditCard',
      color: '#f59e0b'
    },
    {
      id: 5,
      title: 'Valores a Receber',
      value: formatCurrency(kpiData?.pendingReceivables || 0),
      change: `${kpiData?.overdueReceivables || 0} vencidos`,
      trend: kpiData?.overdueReceivables > 0 ? 'down' : 'neutral',
      icon: 'Wallet',
      color: '#3b82f6'
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />
      <BreadcrumbNavigation />
      <main className="w-full">
        <div className="px-6 py-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Financeiro</h1>
              <p className="text-gray-600">Visão completa da posição financeira da JR Wise Tour</p>
            </div>
            <button
              onClick={refreshDashboard}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-700 font-medium">Erro ao carregar dados</p>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                  <button
                    onClick={refreshDashboard}
                    className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Tentar Novamente
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KPI Cards - Load immediately with priority data */}
          <div className="mb-6">
            {kpiLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {[1, 2, 3, 4, 5]?.map((i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {kpiCards?.map((kpi) => (
                  <KPICard key={kpi?.id} {...kpi} />
                ))}
              </div>
            )}
          </div>

          {/* Chart Section - Deferred loading with separate state */}
          <div className="mb-6" data-export-chart="true">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Estatísticas Detalhadas</h2>
                {chartLoading && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Carregando gráficos...</span>
                  </div>
                )}
              </div>
              
              {chartLoading ? (
                <div className="h-80 bg-gray-100 rounded animate-pulse flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando análise detalhada...</p>
                  </div>
                </div>
              ) : (
                <QuickStats 
                  dashboardData={chartData} 
                  loading={false}
                />
              )}
            </div>
          </div>

          {/* Alerts Section - Uses KPI data for immediate display */}
          <div className="mb-6">
            <AlertsSidebar 
              overduePayables={kpiData?.overduePayables || 0}
              overdueReceivables={kpiData?.overdueReceivables || 0}
            />
          </div>

          {/* Recent Transactions - Independent loading */}
          <div className="mb-6">
            <RecentTransactions />
          </div>
        </div>
      </main>
      <QuickActionToolbar />
    </div>
  );
}